// TypeSignatureDialog — type a signature with your own font and rasterize to
// a transparent PNG.
//
// Fonts can be remembered: when "Save this font" is checked, the font file is
// uploaded to the PRIVATE `signature-fonts` bucket scoped to your user. Only
// you can read/use/delete it — even workspace teammates cannot access it.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Trash2, Type, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import {
  loadSignatureFont,
  rasterizeSignatureToPng,
  validateSignatureFontFile,
  type LoadedSignatureFont,
} from "@/lib/signatureRasterizer";
import { uploadBlockImage } from "@/lib/composerBlocks";
import {
  deleteSignatureFont,
  fetchSignatureFontAsFile,
  listSignatureFonts,
  saveSignatureFont,
  type SignatureFont,
} from "@/lib/signatureFonts";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  blockId: string;
  blockTitle?: string;
  /** Preferred seed for the signature text — the printed name on the block. */
  signatureName?: string;
  onInserted: (next: { imageUrl: string; imagePath: string; imageAlt: string }) => void;
};

export const TypeSignatureDialog = ({
  open,
  onOpenChange,
  workspaceId,
  blockId,
  blockTitle,
  signatureName,
  onInserted,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loaded, setLoaded] = useState<LoadedSignatureFont | null>(null);
  const [fontName, setFontName] = useState<string>("");
  const [text, setText] = useState<string>(signatureName?.trim() || blockTitle || "");
  const [fontPx, setFontPx] = useState<number>(64);
  const [color, setColor] = useState<string>("#808080");
  const [padding, setPadding] = useState<number>(6);
  const [licenseOk, setLicenseOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Saved-fonts library
  const [savedFonts, setSavedFonts] = useState<SignatureFont[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedSavedId, setSelectedSavedId] = useState<string>("");
  /** When a font is uploaded fresh (not picked from the library), save it after
   *  the user confirms license. Defaults ON so the user doesn't have to upload
   *  the same font next time. */
  const [saveForReuse, setSaveForReuse] = useState(true);
  /** True when the currently-loaded font came from the saved library (not a
   *  fresh upload), so we shouldn't re-save it. */
  const [loadedFromLibrary, setLoadedFromLibrary] = useState(false);
  /** File object kept around to persist after license is confirmed. */
  const pendingUploadFileRef = useRef<File | null>(null);

  // Reset on open.
  useEffect(() => {
    if (!open) return;
    setText(signatureName?.trim() || blockTitle || "");
    setFontPx(64);
    setColor("#808080");
    setPadding(6);
    setLicenseOk(false);
    setSelectedSavedId("");
    setLoadedFromLibrary(false);
    setSaveForReuse(true);
    pendingUploadFileRef.current = null;
  }, [open, blockTitle, signatureName]);

  // Cleanup font on close / unmount.
  useEffect(() => {
    if (!open && loaded) {
      loaded.cleanup();
      setLoaded(null);
      setFontName("");
    }
  }, [open, loaded]);
  useEffect(() => () => { if (loaded) loaded.cleanup(); }, [loaded]);

  // Load the saved-fonts list when the dialog opens.
  const reloadSavedFonts = useCallback(async () => {
    if (!workspaceId) return;
    setSavedLoading(true);
    try {
      const list = await listSignatureFonts(workspaceId);
      setSavedFonts(list);
    } catch (e) {
      toast({
        title: "Could not load saved fonts",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setSavedLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (open) void reloadSavedFonts();
  }, [open, reloadSavedFonts]);

  const onPickFont = () => fileInputRef.current?.click();

  const loadFile = async (file: File, fromLibrary: boolean) => {
    const err = validateSignatureFontFile(file);
    if (err) {
      toast({ title: "Font not accepted", description: err, variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      if (loaded) loaded.cleanup();
      const next = await loadSignatureFont(file);
      setLoaded(next);
      setFontName(file.name);
      setLoadedFromLibrary(fromLibrary);
      pendingUploadFileRef.current = fromLibrary ? null : file;
    } catch (e2) {
      toast({ title: "Could not load font", description: (e2 as Error).message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const onFontFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSelectedSavedId("");
    await loadFile(file, false);
  };

  const onPickSavedFont = async (id: string) => {
    if (!id) return;
    const font = savedFonts.find((f) => f.id === id);
    if (!font) return;
    setSelectedSavedId(id);
    setLoading(true);
    try {
      const file = await fetchSignatureFontAsFile(font);
      await loadFile(file, true);
    } catch (e) {
      toast({
        title: "Could not load saved font",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const onDeleteSavedFont = async (font: SignatureFont) => {
    if (!confirm(`Delete saved font "${font.displayName}"? This cannot be undone.`)) return;
    try {
      await deleteSignatureFont(font);
      toast({ title: "Saved font deleted" });
      if (selectedSavedId === font.id) {
        setSelectedSavedId("");
        if (loaded) loaded.cleanup();
        setLoaded(null);
        setFontName("");
      }
      await reloadSavedFonts();
    } catch (e) {
      toast({
        title: "Could not delete font",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  const previewStyle = useMemo<React.CSSProperties>(() => ({
    fontFamily: loaded ? `"${loaded.family}", cursive` : "cursive",
    fontSize: `${Math.min(fontPx, 96)}px`,
    color,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }), [loaded, fontPx, color]);

  const canInsert = !!loaded && licenseOk && text.trim().length > 0 && !busy;

  const onInsert = async () => {
    if (!loaded) return;
    setBusy(true);
    try {
      // If this was a fresh upload and the user opted to remember it, save the
      // font file to the private library BEFORE rasterizing so a failure here
      // doesn't leave a half-baked block.
      const pending = pendingUploadFileRef.current;
      if (pending && !loadedFromLibrary && saveForReuse) {
        try {
          await saveSignatureFont({
            workspaceId,
            file: pending,
            displayName: pending.name.replace(/\.(woff2?|otf|ttf)$/i, ""),
            licenseConfirmed: licenseOk,
          });
          pendingUploadFileRef.current = null;
          // Don't block insertion on reload; happens in background.
          void reloadSavedFonts();
        } catch (e) {
          // Saving for reuse is non-fatal — surface a toast but continue.
          toast({
            title: "Couldn't remember this font",
            description: (e as Error).message,
            variant: "destructive",
          });
        }
      }

      const file = await rasterizeSignatureToPng({
        text: text.trim(),
        family: loaded.family,
        fontPx,
        color,
        padding,
      });
      const up = await uploadBlockImage(workspaceId, blockId, file);
      onInserted({
        imageUrl: up.signedUrl,
        imagePath: up.path,
        imageAlt: text.trim(),
      });
      onOpenChange(false);
      toast({ title: "Signature inserted" });
    } catch (e) {
      toast({ title: "Could not insert signature", description: (e as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Type className="h-4 w-4" />
            Type signature with my font
          </DialogTitle>
          <DialogDescription>
            Pick a font you've saved before, or upload a new one. Saved fonts are
            stored privately in your account — only you can use or delete them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Saved fonts picker */}
          {(savedFonts.length > 0 || savedLoading) && (
            <div className="space-y-1.5">
              <Label>Saved fonts</Label>
              <div className="flex items-center gap-2">
                <Select
                  value={selectedSavedId}
                  onValueChange={onPickSavedFont}
                  disabled={savedLoading || loading}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue
                      placeholder={savedLoading ? "Loading…" : "Choose a saved font"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {savedFonts.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedSavedId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title="Delete this saved font"
                    onClick={() => {
                      const f = savedFonts.find((x) => x.id === selectedSavedId);
                      if (f) void onDeleteSavedFont(f);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Font upload */}
          <div className="space-y-1.5">
            <Label>{savedFonts.length > 0 ? "…or upload a new font" : "Font file"}</Label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onPickFont} disabled={loading}>
                {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
                {loaded && !loadedFromLibrary ? "Replace font" : "Upload .woff2 / .otf / .ttf"}
              </Button>
              {fontName && (
                <span className="truncate text-xs text-muted-foreground" title={fontName}>{fontName}</span>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".woff2,.woff,.otf,.ttf"
              className="hidden"
              onChange={onFontFile}
            />
            {loaded && !loadedFromLibrary && (
              <div className="flex items-start gap-2 pt-1">
                <Checkbox
                  id="sig-save-reuse"
                  checked={saveForReuse}
                  onCheckedChange={(v) => setSaveForReuse(v === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="sig-save-reuse" className="text-xs font-normal leading-snug">
                  Save this font to my account so I don't have to upload it again.
                  Stored privately — only you can access or delete it.
                </Label>
              </div>
            )}
          </div>

          {/* Signature text */}
          <div className="space-y-1.5">
            <Label htmlFor="sig-text">Signature text</Label>
            <Input
              id="sig-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. Jane Doe"
            />
          </div>

          {/* Size + color */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Size: {fontPx}px</Label>
              <Slider
                value={[fontPx]}
                min={24}
                max={120}
                step={2}
                onValueChange={(v) => setFontPx(v[0] ?? 64)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sig-color">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="sig-color"
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 w-12 cursor-pointer p-1"
                />
                <Input
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-9 flex-1"
                />
              </div>
            </div>
          </div>

          {/* Padding around the rasterized signature */}
          <div className="space-y-1.5">
            <Label>Padding around signature: {padding}px</Label>
            <Slider
              value={[padding]}
              min={0}
              max={16}
              step={1}
              onValueChange={(v) => setPadding(v[0] ?? 6)}
            />
            <p className="text-[11px] text-muted-foreground">
              Trims transparent space around the inked text. Lower = tighter, more horizontal.
            </p>
          </div>

          {/* Preview */}
          <div className="space-y-1.5">
            <Label>Preview</Label>
            <div className="flex min-h-[96px] items-center justify-start rounded-md border border-border/60 bg-background/50 p-3">
              {loaded ? (
                <div style={previewStyle}>{text.trim() || "Your signature"}</div>
              ) : (
                <p className="text-xs text-muted-foreground">Upload or pick a font to preview your signature.</p>
              )}
            </div>
          </div>

          {/* License */}
          <div className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/30 p-3">
            <Checkbox
              id="sig-license"
              checked={licenseOk}
              onCheckedChange={(v) => setLicenseOk(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="sig-license" className="text-xs font-normal leading-snug">
              I confirm my license for this font permits me to use it for personal or commercial signatures.
              If I save it for reuse, the font is stored privately in my own account — never shared with
              workspace teammates and never made public.
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onInsert} disabled={!canInsert}>
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Insert signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TypeSignatureDialog;
